import _ from "lodash";
import moment from "moment-timezone";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { toast } from "react-toastify";
import styled from "styled-components";

import { IProduct } from "src/core/products/api/products.api";
import { approvalChangeProductsTableColumns } from "src/core/shared/components/tables/approvalChangeProductsTableColumns";
import { Select } from "src/library/components/Forms";
import Grid from "src/library/components/Hedron";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import Table, { ITableInstance } from "src/library/components/Tables/Table";
import { Text } from "src/library/components/Text";
import { FilterParams } from "src/library/utils";
import {
  IProductApprovalList,
  ProductApprovalRequestName,
  useProductApprovalApi
} from "src/modules/product-approval/api/product-approval.api";
import { PageProps } from "src/pages/Router/routes";

import NoApprovalChangeDataFound from "./NoApprovalChangeDataFound";

export interface ApprovalChangeProductsTabProps {
  startDate: string | string[] | null | undefined;
  approvalList: IProductApprovalList | null;
}

/**
 * displays
 *   - a list of products by selected active product approval change date
 *   - a select component to navigate between product approval change dates
 */
const ApprovalChangeProductsTab: React.ReactFCWithChildren<PageProps<ApprovalChangeProductsTabProps>> = ({
  startDate,
  approvalList,
  ...props
}) => {
  const { history, terminology } = props;
  const [ listApprovalChangeDatesResponse, listApprovalChangeDatesRequest ] = useProductApprovalApi(ProductApprovalRequestName.GET_APPROVAL_CHANGE_DATES);
  const [ listApprovalChangeProductsResponse, listApprovalChangeProductsRequest ] = useProductApprovalApi(ProductApprovalRequestName.GET_APPROVAL_LIST_PRODUCTS);
  const [ pageFilters, setPageFilters ] = useState<{ startDate: string; productApprovalListId: number } | null>(null);
  const [ , setFilters ] = useState<FilterParams<IProduct>>({});
  const TableRef = useRef<ITableInstance<IProduct>>(null);
  const productApprovalListId = approvalList?.id;

  // list approval change dates by approvalListId
  useEffect(() => {
    if (productApprovalListId) {
      listApprovalChangeDatesRequest({ productApprovalListId });
    }
  }, [ productApprovalListId, listApprovalChangeDatesRequest ]);

  useEffect(() => {
    if (pageFilters) {
      listApprovalChangeProductsRequest(pageFilters);
    }
  }, [ listApprovalChangeProductsRequest, pageFilters ]);

  // the fetch for products by approval and start date to populate the table
  const onFetchData = useCallback(
    (filters: FilterParams<IProduct>) => {
      setFilters(filters);
    },
    [ ]
  );

  // reload the table
  useEffect(() => {
    if (pageFilters) {
      if (TableRef.current) {
        TableRef.current.reloadTable(true);
      }
    }
  }, [ pageFilters, TableRef ]);

  // handle approval change dates response
  useEffect(() => {
    if (listApprovalChangeDatesResponse.data && listApprovalChangeDatesResponse.data.length) {
      // set active to be first if not set in url already
      if (!startDate) {
        history.push(`?startDate=${listApprovalChangeDatesResponse.data[ listApprovalChangeDatesResponse.data.length - 1 ].startDate}`);
      }
    }

    if (listApprovalChangeDatesResponse.error) {
      toast.error(listApprovalChangeDatesResponse.error.message);
    }
  }, [
    history,
    listApprovalChangeDatesResponse,
    startDate
  ]);

  // handle approval changes to the productApprovalListId and the date
  useEffect(() => {
    if (startDate && productApprovalListId) {
      setPageFilters({
        startDate: startDate as string,
        productApprovalListId
      });
    }

    // if remove date from url
    if (!startDate && productApprovalListId) {
      listApprovalChangeDatesRequest({ productApprovalListId });
    }
  }, [
    startDate,
    productApprovalListId,
    listApprovalChangeDatesRequest
  ]);

  if (!startDate) {
    return <NoApprovalChangeDataFound name={approvalList?.name} />;
  }

  // return loader if request price change dates requests pending
  if (listApprovalChangeDatesResponse.pending) {
    return (
      <StandAloneLoaderWrapper>
        <LoadingSpinner type="circular" />
      </StandAloneLoaderWrapper>
    );
  }

  return (
    <>
      {listApprovalChangeDatesResponse.data && listApprovalChangeDatesResponse.data.length === 0 ?
        (
          <NoApprovalChangeDataFound name={approvalList?.name} />
        ) : (
          <>
            <Select
              as="select"
              label="Active Approval Change Dates"
              name="active-approval-change-dates"
              value={`/admin/products/approval-lists/${productApprovalListId}/changes?startDate=${startDate}`}
              handleChange={e => history.push(e.target.value)}
              options={_.map(listApprovalChangeDatesResponse.data, approvalChangeDate => ({
                text: moment.utc(approvalChangeDate.startDate, "YYYY-MM-DD").format("ddd D MMM YYYY"),
                value: `/admin/products/approval-lists/${approvalChangeDate.productApprovalListId}/changes?startDate=${approvalChangeDate.startDate}`
              }))}
            />

            <Grid.Box className="page-desc">
              <Text as="h4">
                {`Showing product approval changes as they were on ${moment
                  .utc(startDate as string)
                  .local()
                  .format("ddd D MMM YYYY")}`}
              </Text>
            </Grid.Box>

            <Grid.Bounds className="table-content">

              <Grid.Box width="95%">
                {pageFilters && (
                  <>
                    {listApprovalChangeProductsResponse.pending ? (
                      <LoadingSpinner
                        type="flat"
                        position="inline"
                      />
                    ) : (
                      <Table
                        tableId={`approvalListId:${productApprovalListId}`}
                        ref={TableRef}
                        tableOf={`${terminology.product.singular.text} prices`}
                        data={listApprovalChangeProductsResponse.data ? listApprovalChangeProductsResponse.data : []}
                        columns={approvalChangeProductsTableColumns(terminology)}
                        loading={listApprovalChangeProductsResponse.pending}
                        error={listApprovalChangeProductsResponse.error}
                        onFetchData={onFetchData}
                        pages={1}
                        totalRows={listApprovalChangeProductsResponse.data ? listApprovalChangeProductsResponse.data.length : 0}
                      />
                    )}

                  </>
                )}
              </Grid.Box>
            </Grid.Bounds>
          </>
        )}
    </>
  );
};

const StandAloneLoaderWrapper = styled.div`
   min-height: 300px; 
`;

export default ApprovalChangeProductsTab;
